import classNames from 'classnames'
import { Plus } from 'iconoir-react'
import moment from 'moment'
import { useState } from 'react'
import { Button } from 'app/components/ui/button'
import { DAY_KEY, TASK_DRAG_TYPE, visibleRange } from 'lib/taskCalendar'
import type { TaskJson } from 'app/types/entities'
import { TaskCalendarChip } from './TaskCalendarChip'

/** Day columns, not an hourly grid: a due date has no time of day. */
export function TaskCalendarWeek({
  anchor,
  tasksByDay,
  canDrag,
  onOpenTask,
  onToggleStatus,
  onReschedule,
  onCreate,
  onOpenDay,
}: {
  anchor: moment.Moment
  tasksByDay: Map<string, TaskJson[]>
  canDrag: boolean
  onOpenTask: (task: TaskJson) => void
  onToggleStatus: (task: TaskJson) => void
  onReschedule: (taskId: string, day: moment.Moment) => void
  onCreate: (day: moment.Moment) => void
  onOpenDay: (day: moment.Moment) => void
}) {
  const [dropTarget, setDropTarget] = useState<string>()
  const { start } = visibleRange(`week`, anchor)
  const week = Array.from({ length: 7 }, (_, i) => start.clone().add(i, `days`))
  const today = moment().format(DAY_KEY)

  return (
    <div className="grid min-h-0 flex-1 grid-cols-7 overflow-hidden rounded-md border border-default bg-surface">
      {week.map((day) => {
        const key = day.format(DAY_KEY)
        const isToday = key === today
        return (
          <section
            key={key}
            aria-label={day.format(`dddd, MMMM D`)}
            className={classNames(
              `group/day flex min-h-0 min-w-0 flex-col border-default [&:not(:first-child)]:border-l`,
              dropTarget === key && `bg-primary-alpha-8`,
            )}
            onDragOver={(e) => {
              if (!e.dataTransfer.types.includes(TASK_DRAG_TYPE)) return
              e.preventDefault()
              setDropTarget(key)
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropTarget(undefined)
            }}
            onDrop={(e) => {
              setDropTarget(undefined)
              const taskId = e.dataTransfer.getData(TASK_DRAG_TYPE)
              if (taskId) onReschedule(taskId, day)
            }}
          >
            {/* Same grey header band as the month grid and the tasks table. */}
            <div className="border-b border-default bg-surface-subtle">
              <Button variant="ghost" size="row" onClick={() => onOpenDay(day)}>
                <span className="flex w-full flex-col items-center gap-1 py-1">
                  <span className="text-caption-sm font-medium uppercase tracking-wide text-subtle">
                    {day.format(`ddd`)}
                  </span>
                  <span
                    className={classNames(
                      `flex h-8 w-8 items-center justify-center rounded-full text-heading-xs`,
                      isToday ? `bg-primary-600 text-on-color` : `text-strong`,
                    )}
                  >
                    {day.date()}
                  </span>
                </span>
              </Button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-1.5">
              {(tasksByDay.get(key) ?? []).map((task) => (
                <TaskCalendarChip
                  key={task.id}
                  task={task}
                  variant="compact"
                  draggable={canDrag}
                  onOpen={() => onOpenTask(task)}
                  onToggleStatus={() => onToggleStatus(task)}
                />
              ))}
              {/* Shown on hover; always shown where there is no hover. */}
              <span className="opacity-0 transition-opacity focus-within:opacity-100 group-hover/day:opacity-100 pointer-coarse:opacity-100">
                <Button variant="subtle" size="xs" onClick={() => onCreate(day)}>
                  <Plus />
                  New Task
                </Button>
              </span>
            </div>
          </section>
        )
      })}
    </div>
  )
}
